import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";

const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

/**
 * Manages the storage of the access token in localStorage upon authentication state changes.
 *
 * - When the user becomes authenticated, it acquires the access token silently
 *   and stores it in localStorage under the key "access_token".
 */
function TokenManager({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (isAuthenticated) {
      // User just authenticated - store the token
      getAccessTokenSilently()
        .then((token) => {
          localStorage.setItem("access_token", token);
        })
        .catch((error) => {
          console.error("Failed to acquire access token silently:", error);
        });
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  return <>{children}</>;
}

export function AuthProvider({ children }: AuthenticationProviderProps) {
  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience
      }}
    >
      <TokenManager>{children}</TokenManager>
    </Auth0Provider>
  );
}
